import React, { createContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase/firebase';
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    GoogleAuthProvider, // Import GoogleAuthProvider
    signInWithPopup // Import signInWithPopup
} from "firebase/auth";
import { doc, setDoc, getDoc, Timestamp } from "firebase/firestore";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    // Helper function to calculate aggregated permissions from subscriptions
    const calculateAggregatedPermissions = (subscriptions) => {
        const activeSubscriptions = subscriptions.filter(sub => sub.status === 'active');
        
        if (activeSubscriptions.length === 0) {
            return {
                canViewReports: false,
                canViewCharts: false,
                maxStocksPerQuarter: 0,
                riskLevelAccess: [],
                features: [],
                activePlans: []
            };
        }
        
        // Aggregate permissions from all active plans
        const maxStocksPerQuarter = activeSubscriptions.reduce((sum, sub) => {
            // This will be enhanced when we have plan data available
            return sum + (sub.maxStocksPerQuarter || 0);
        }, 0);
        
        const riskLevelAccess = [...new Set(
            activeSubscriptions.flatMap(sub => {
                return Array.from({length: sub.riskLevel || 1}, (_, i) => i + 1);
            })
        )].sort();
        
        const features = [...new Set(
            activeSubscriptions.flatMap(sub => sub.features || [])
        )];
        
        const canViewCharts = riskLevelAccess.some(level => level >= 3);
        
        return {
            canViewReports: true,
            canViewCharts,
            maxStocksPerQuarter,
            riskLevelAccess,
            features,
            activePlans: activeSubscriptions.map(sub => sub.planName)
        };
    };

    // Helper function to normalize user status
    const normalizeUserStatus = (status) => {
        if (!status) return 'non-approved';
        
        // Normalize common status variations
        const normalizedStatus = status.toLowerCase().trim();
        
        // Map various status values to standard ones
        const statusMap = {
            'pending': 'non-approved',
            'not approved': 'non-approved',
            'not_approved': 'non-approved',
            'notapproved': 'non-approved',
            'approved': 'approved',
            'active': 'approved',
            'suspended': 'suspended',
            'inactive': 'suspended'
        };
        
        return statusMap[normalizedStatus] || 'non-approved';
    };

    // Check if user is approved and can access features
    const isUserApproved = (user) => {
        return user && normalizeUserStatus(user.status) === 'approved';
    };

    // Check if user can access reports (approved + has permissions)
    const canUserAccessReports = (user) => {
        return isUserApproved(user) && user.permissions && user.permissions.canViewReports;
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseAuthUser) => {
            if (firebaseAuthUser) {
                const userRef = doc(db, "users", firebaseAuthUser.uid);
                let docSnap = await getDoc(userRef);
                let userDataFromFirestore;

                if (!docSnap.exists()) {
                    console.warn(`Firestore document missing for user ${firebaseAuthUser.uid} during onAuthStateChanged. Attempting to create.`);
                    const isGoogleProvider = firebaseAuthUser.providerData.some(p => p.providerId === 'google.com');
                    
                    // Initialize with new structure
                    const initialSubscriptions = [];
                    const initialPermissions = calculateAggregatedPermissions(initialSubscriptions);
                    
                    userDataFromFirestore = {
                        uid: firebaseAuthUser.uid,
                        name: firebaseAuthUser.displayName || 'User',
                        email: firebaseAuthUser.email,
                        role: 'basic',
                        provider: isGoogleProvider ? 'google' : (firebaseAuthUser.providerData.length > 0 ? firebaseAuthUser.providerData[0].providerId : 'email-password'),
                        customer_id: '',
                        // New multiple subscriptions structure
                        subscriptions: initialSubscriptions,
                        permissions: initialPermissions,
                        status: 'non-approved', // Default to 'non-approved' for new users
                        // Metadata
                        createdAt: Timestamp.now(),
                        updatedAt: Timestamp.now(),
                        lastLoginAt: Timestamp.now()
                    };

                    try {
                        await setDoc(userRef, userDataFromFirestore);
                        docSnap = await getDoc(userRef); 
                        if (docSnap.exists()) {
                            userDataFromFirestore = docSnap.data();
                        }
                    } catch (error) {
                        console.error(`Failed to create missing Firestore document for user ${firebaseAuthUser.uid} in onAuthStateChanged:`, error);
                    }
                } else {
                    userDataFromFirestore = docSnap.data();
                    
                    // Migrate old structure to new if needed
                    if (userDataFromFirestore.subscription && !userDataFromFirestore.subscriptions) {
                        console.log('Migrating user to new subscription structure');
                        const oldSubscription = userDataFromFirestore.subscription;
                        const newSubscriptions = [{
                            planId: oldSubscription.planId || 'prime',
                            planName: oldSubscription.planName || 'Prime',
                            status: oldSubscription.status || 'active',
                            startDate: oldSubscription.startDate || Timestamp.now(),
                            endDate: oldSubscription.endDate || Timestamp.fromDate(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)),
                            autoRenew: oldSubscription.autoRenew || true,
                            purchasedAt: userDataFromFirestore.createdAt || Timestamp.now(),
                            order: 1
                        }];
                        
                        const newPermissions = calculateAggregatedPermissions(newSubscriptions);
                        
                        // Update user with new structure
                        await setDoc(userRef, {
                            ...userDataFromFirestore,
                            subscriptions: newSubscriptions,
                            permissions: newPermissions,
                            updatedAt: Timestamp.now()
                        }, { merge: true });
                        
                        userDataFromFirestore = {
                            ...userDataFromFirestore,
                            subscriptions: newSubscriptions,
                            permissions: newPermissions
                        };
                    }
                }

                setUser({
                    firebaseUser: firebaseAuthUser,
                    ...userDataFromFirestore,
                    uid: firebaseAuthUser.uid,
                    email: firebaseAuthUser.email,
                    name: userDataFromFirestore.name || firebaseAuthUser.displayName || 'User',
                    role: userDataFromFirestore.role || 'basic',
                    customerId: userDataFromFirestore.customer_id || null,
                    status: normalizeUserStatus(userDataFromFirestore.status), // Use normalized status
                });
            } else {
                setUser(null);
            }
            setLoading(false);
        });

        return unsubscribe;
    }, []);

    const register = async (name, email, password) => {
        setLoading(true);
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            // Initialize with new structure
            const initialSubscriptions = [];
            const initialPermissions = calculateAggregatedPermissions(initialSubscriptions);

            // Store additional user data in Firestore
            const userRef = doc(db, "users", user.uid);
            await setDoc(userRef, {
                uid: user.uid,
                name: name,
                email: email,
                role: 'basic',
                provider: 'email-password',
                customer_id: '',
                // New multiple subscriptions structure
                subscriptions: initialSubscriptions,
                permissions: initialPermissions,
                status: 'non-approved', // Default to 'non-approved' for new users
                // Metadata
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now(),
                lastLoginAt: Timestamp.now()
            });
            return user;

        } catch (error) {
            console.error("Registration Error:", error);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const login = async (email, password) => {
        setLoading(true);
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            // onAuthStateChanged will handle setting the user
            return userCredential.user;
        } catch (error) {
            console.error("Login Error: ", error);
            throw error;
        }
        finally {
            setLoading(false);
        }
    };

    const googleLogin = async () => {
        setLoading(true);
        try {
            const provider = new GoogleAuthProvider();
            const userCredential = await signInWithPopup(auth, provider);
            const firebaseAuthUser = userCredential.user;

            const userRef = doc(db, "users", firebaseAuthUser.uid);
            let docSnap = await getDoc(userRef);

            let userDataFromFirestore;

            if (!docSnap.exists()) {
                // User document doesn't exist in Firestore, create it
                const initialSubscriptions = [];
                const initialPermissions = calculateAggregatedPermissions(initialSubscriptions);
                
                const newUserFirestoreData = {
                    uid: firebaseAuthUser.uid,
                    name: firebaseAuthUser.displayName || 'User',
                    email: firebaseAuthUser.email,
                    role: 'basic',
                    provider: 'google',
                    customer_id: '',
                    // New multiple subscriptions structure
                    subscriptions: initialSubscriptions,
                    permissions: initialPermissions,
                    status: 'non-approved', // Default to 'non-approved' for new users
                    // Metadata
                    createdAt: Timestamp.now(),
                    updatedAt: Timestamp.now(),
                    lastLoginAt: Timestamp.now()
                };
                await setDoc(userRef, newUserFirestoreData);
                userDataFromFirestore = newUserFirestoreData;
            } else {
                // User document already exists
                userDataFromFirestore = docSnap.data();
                
                // Migrate old structure to new if needed
                if (userDataFromFirestore.subscription && !userDataFromFirestore.subscriptions) {
                    console.log('Migrating user to new subscription structure');
                    const oldSubscription = userDataFromFirestore.subscription;
                    const newSubscriptions = [{
                        planId: oldSubscription.planId || 'prime',
                        planName: oldSubscription.planName || 'Prime',
                        status: oldSubscription.status || 'active',
                        startDate: oldSubscription.startDate || Timestamp.now(),
                        endDate: oldSubscription.endDate || Timestamp.fromDate(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)),
                        autoRenew: oldSubscription.autoRenew || true,
                        purchasedAt: userDataFromFirestore.createdAt || Timestamp.now(),
                        order: 1
                    }];
                    
                    const newPermissions = calculateAggregatedPermissions(newSubscriptions);
                    
                    // Update user with new structure
                    await setDoc(userRef, {
                        ...userDataFromFirestore,
                        subscriptions: newSubscriptions,
                        permissions: newPermissions,
                        updatedAt: Timestamp.now()
                    }, { merge: true });
                    
                    userDataFromFirestore = {
                        ...userDataFromFirestore,
                        subscriptions: newSubscriptions,
                        permissions: newPermissions
                    };
                }
            }

            // Construct the full user object for the context
            const fullUserContextObject = {
                firebaseUser: firebaseAuthUser,
                uid: firebaseAuthUser.uid,
                email: firebaseAuthUser.email,
                name: userDataFromFirestore.name || firebaseAuthUser.displayName || 'User',
                role: userDataFromFirestore.role || 'basic',
                customerId: userDataFromFirestore.customer_id || null,
                status: userDataFromFirestore.status || 'non-approved', // Default to 'non-approved' if status is missing
                subscriptions: userDataFromFirestore.subscriptions || [],
                permissions: userDataFromFirestore.permissions || calculateAggregatedPermissions([]),
            };

            setUser(fullUserContextObject);

            return firebaseAuthUser;

        } catch (error) {
            console.error("Google Login Error: ", error);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const logout = async () => {
        await signOut(auth);
        // User is automatically set to null by onAuthStateChanged
    };

    if (loading) {
        return <div>Loading...</div>; // Or a better loading indicator
    }

    return (
        <AuthContext.Provider value={{ user, register, login, googleLogin, logout }}>
            {children}
        </AuthContext.Provider>
    );
};